import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { de, it, es, ru, zh, ar } from "./translations";

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "ar", label: "العربية", short: "AR" },
  { code: "zh", label: "中文", short: "ZH" },
  { code: "it", label: "Italiano", short: "IT" },
  { code: "es", label: "Español", short: "ES" },
  { code: "de", label: "Deutsch", short: "DE" },
  { code: "ru", label: "Русский", short: "RU" },
];

const en = {
  nav: { home: "Home", services: "Services", fleet: "Fleet", experience: "Experience", corporate: "Corporate", partners: "Partners", destinations: "Destinations", about: "About", contact: "Contact" },
  cta: { request: "Request a Journey", becomePartner: "Become a Partner", discoverFleet: "Discover Our Fleet", openCorporate: "Open a Corporate Account", planEvent: "Plan Your Event" },
  hero: {
    line1: "LUXURY MOBILITY.",
    line2: "WITHOUT LIMITS.",
    sub: "Private chauffeur services for those who expect more.",
    desc: "Europe Luxury Transfers provides premium chauffeur and luxury transportation throughout France and across Europe, combining exceptional vehicles, professional multilingual chauffeurs and discreet, highly personalized service.",
    available: "Available 24/7",
    tagline: "France · Europe · Any Destination",
  },
  manifesto: {
    eyebrow: "More Than a Transfer",
    title: "MORE THAN A TRANSFER. A HIGHER STANDARD.",
    body: "Europe Luxury Transfers believes that luxury transportation should be effortless. From the first request to the final destination, every detail is carefully coordinated to provide a seamless experience defined by punctuality, discretion, comfort and personal attention.",
    statement1: "Our chauffeurs do more than drive.",
    statement2: "They anticipate.",
    chapters: [
      { n: "I", title: "Discretion", body: "Confidentiality is not a feature. It is the foundation of every journey we coordinate." },
      { n: "II", title: "Excellence", body: "Exceptional vehicles, impeccable chauffeurs and an uncompromising standard of care." },
      { n: "III", title: "Precision", body: "Flight monitoring, route planning and timing coordinated to the minute." },
      { n: "IV", title: "Comfort", body: "An environment designed for calm — from the first door opened to the last." },
      { n: "V", title: "Professionalism", body: "Multilingual, trained and protocol-aware. Representatives of a European standard." },
    ],
  },
  services: {
    eyebrow: "Our Services",
    title: "A SERVICE FOR EVERY OCCASION.",
    items: [
      { title: "Private Chauffeur", body: "Personal chauffeur services for individuals, families and VIP clients." },
      { title: "Airport & Private Aviation", body: "Airport transfers, VIP terminals, FBOs, private aviation, helicopter transfers, meet & greet, flight monitoring and luggage assistance." },
      { title: "Executive Mobility", body: "Executive transportation, business meetings, roadshows, corporate travel and multi-city business itineraries." },
      { title: "VIP & Delegation", body: "Discreet transportation for VIP guests, diplomatic delegations, institutional visits and protocol-sensitive movements." },
      { title: "Hotel & Concierge", body: "Transportation for luxury hotels, resorts, concierge companies and their guests." },
      { title: "Events & Occasions", body: "Weddings, fashion events, conferences, exhibitions, film productions, sporting events and VIP events." },
      { title: "Private Journeys", body: "Multi-day chauffeur services, European road journeys, luxury shopping, sightseeing and fully customized itineraries." },
    ],
  },
  fleet: {
    eyebrow: "Our Fleet",
    title: "EXCLUSIVE COLLECTIONS.",
    onRequest: "Available Upon Request",
    partnerNetwork: "Available Through Our European Partner Network",
    passengers: "Passengers",
    luggage: "Luggage",
    requestVehicle: "Request This Vehicle",
  },
  chauffeur: {
    eyebrow: "The Chauffeur Standard",
    title: "OUR CHAUFFEURS DO MORE THAN DRIVE. THEY ANTICIPATE.",
    body: "Our chauffeurs are professional representatives of the Europe Luxury Transfers experience.",
    standards: ["Impeccable presentation", "Professional attire", "Punctuality", "Discretion", "Multilingual communication", "Local expertise", "Defensive driving", "VIP protocol awareness", "Luggage assistance", "Door-to-door service", "Personalized attention", "Professional communication"],
  },
  discretion: {
    eyebrow: "Discretion & VIP Protocol",
    title: "DISCRETION IS PART OF THE SERVICE.",
    body: "VIP transportation requires more than a luxury vehicle. Our service is designed around:",
    items: ["Confidentiality", "Privacy", "Discreet coordination", "Protocol-sensitive movements", "VIP arrival coordination", "Multi-vehicle coordination", "Executive itineraries", "Hotel coordination", "Airport coordination", "Flexible itinerary management"],
    statement: "EVERY DETAIL IS COORDINATED. EVERY MOVEMENT IS DISCREET.",
  },
  destinations: {
    eyebrow: "France & Europe",
    title: "ONE NETWORK. ANY DESTINATION.",
    body: "Europe Luxury Transfers provides luxury transportation throughout France and across Europe, operating through a professional European network.",
    france: "France",
    europe: "Europe",
    franceMore: "and destinations throughout France",
    europeMore: "and destinations throughout Europe",
    statement: "FRANCE · EUROPE · ANY DESTINATION",
  },
  aviation: {
    eyebrow: "Airport & Private Aviation",
    title: "FROM PRIVATE AVIATION TO PRIVATE MOBILITY.",
    items: ["Airport transfers", "VIP terminal transfers", "FBO transfers", "Private jet transportation", "Helicopter transfers", "Meet & greet", "Flight monitoring", "Delayed-flight coordination", "Luggage assistance", "Hotel–airport transfers"],
  },
  corporate: {
    eyebrow: "Corporate Mobility",
    title: "A EUROPEAN MOBILITY PARTNER FOR BUSINESS.",
    body: "For international companies, executives, corporate travel managers, luxury hotels, travel agencies, concierge companies, event agencies and private aviation companies.",
    items: ["Executive transfers", "Business meetings", "Roadshows", "Multi-city itineraries", "Dedicated chauffeurs", "Corporate accounts", "Centralized booking", "Digital invoices", "Booking history", "Dedicated account management"],
  },
  partners: {
    eyebrow: "Hotel & Concierge Partners",
    title: "A PARTNER YOUR CLIENTS CAN TRUST.",
    body: "We work alongside five-star hotels, palace hotels, luxury resorts, concierge companies, travel agencies, tour operators, luxury real estate companies, wedding planners, event planners and private aviation companies.",
  },
  journeys: {
    eyebrow: "Private European Journeys",
    title: "ONE JOURNEY. MULTIPLE DESTINATIONS. ONE STANDARD.",
    offers: ["One-way journeys", "Round trips", "Full-day chauffeur", "Multi-day chauffeur", "Multi-city itineraries", "Bespoke European journeys"],
  },
  pillars: {
    eyebrow: "Client Experience",
    title: "EVERY DETAIL MATTERS.",
    items: ["Punctuality", "Discretion", "Comfort", "Professionalism", "Personalization", "Reliability"],
  },
  about: {
    eyebrow: "About Europe Luxury Transfers",
    title: "MORE THAN A TRANSFER. A STANDARD OF SERVICE.",
    body1: "Europe Luxury Transfers was created around a simple philosophy: luxury transportation should be effortless.",
    body2: "Clients should not have to think about the vehicle, timing, route, luggage, coordination or small details. Our team anticipates them.",
    body3: "We combine premium vehicles, professional chauffeurs, multilingual service and European operational coordination to create a seamless experience from departure to arrival.",
    feel: ["Expected.", "Respected.", "Comfortable.", "Confident.", "In control."],
  },
  form: {
    step1: "Tell Us About Your Journey",
    step2: "Choose Your Experience",
    step3: "Personalize Your Journey",
    step4: "Your Details",
    pickup: "Pickup",
    destination: "Destination",
    date: "Date",
    time: "Time",
    passengers: "Passengers",
    experiences: ["Executive", "Luxury", "VIP", "Van / Group", "Exotic", "Vehicle on request"],
    childSeats: "Child seats",
    meetGreet: "Meet & greet",
    multipleStops: "Multiple stops",
    luggage: "Luggage details",
    languagePref: "Language preference",
    notes: "Special requirements or other requests",
    name: "Full name",
    email: "Email",
    phone: "Phone",
    back: "Back",
    next: "Continue",
    submit: "Submit Request",
    successTitle: "OUR TEAM WILL COORDINATE THE DETAILS.",
    successBody: "Thank you. Your journey request has been received. Our team will contact you shortly with confirmation and complete journey information.",
    reference: "Reference",
    company: "Company",
    contactName: "Contact name",
    partnerType: "Partner type",
    monthlyVolume: "Estimated monthly journeys",
    message: "Message",
    partnerTypes: ["Five-star hotel", "Palace hotel", "Luxury resort", "Concierge company", "Travel agency", "Tour operator", "Luxury real estate", "Wedding planner", "Event planner", "Private aviation", "Other"],
    partnerSuccess: "Thank you. Our partnerships team will contact you shortly.",
    corporateSuccess: "Thank you. Our corporate team will contact you shortly.",
    required: "Please complete the required fields.",
  },
  footer: {
    signature: "LUXURY MOBILITY. WITHOUT LIMITS.",
    tagline: "FRANCE · EUROPE · ANY DESTINATION",
    contact: "Contact",
    navigation: "Navigation",
    languages: "Languages",
    rights: "All rights reserved.",
  },
  marquee: "Discretion · Excellence · Précision · Comfort · Professionalism · Paris · Monaco · Genève · Milan · London ·",
  misc: { available247: "AVAILABLE 24/7", availBody: "Around the clock for planned journeys and transportation requirements, subject to vehicle and chauffeur availability.", yourLanguage: "YOUR LANGUAGE. YOUR JOURNEY.", langBody: "Our international service can support:" },
};

const fr = {
  nav: { home: "Accueil", services: "Services", fleet: "Flotte", experience: "Expérience", corporate: "Entreprises", partners: "Partenaires", destinations: "Destinations", about: "À propos", contact: "Contact" },
  cta: { request: "Demander un trajet", becomePartner: "Devenir partenaire", discoverFleet: "Découvrir notre flotte", openCorporate: "Ouvrir un compte entreprise", planEvent: "Planifier votre événement" },
  hero: {
    line1: "LA MOBILITÉ DE LUXE.",
    line2: "SANS LIMITES.",
    sub: "Services de chauffeur privé pour ceux qui attendent davantage.",
    desc: "Europe Luxury Transfers propose des services de chauffeur et de transport de luxe dans toute la France et à travers l'Europe, alliant véhicules d'exception, chauffeurs professionnels multilingues et service discret, hautement personnalisé.",
    available: "Disponible 24h/24, 7j/7",
    tagline: "France · Europe · Toute destination",
  },
  manifesto: {
    eyebrow: "Plus qu'un transfert",
    title: "PLUS QU'UN TRANSFERT. UN STANDARD SUPÉRIEUR.",
    body: "Europe Luxury Transfers est convaincu que le transport de luxe doit être sans effort. De la première demande à la destination finale, chaque détail est soigneusement coordonné pour offrir une expérience fluide, définie par la ponctualité, la discrétion, le confort et l'attention personnelle.",
    statement1: "Nos chauffeurs font plus que conduire.",
    statement2: "Ils anticipent.",
    chapters: [
      { n: "I", title: "Discrétion", body: "La confidentialité n'est pas une option. C'est le fondement de chaque trajet que nous coordonnons." },
      { n: "II", title: "Excellence", body: "Des véhicules d'exception, des chauffeurs impeccables et un niveau d'exigence sans compromis." },
      { n: "III", title: "Précision", body: "Suivi des vols, planification des itinéraires et coordination à la minute près." },
      { n: "IV", title: "Confort", body: "Un environnement pensé pour la sérénité — de la première portière ouverte à la dernière." },
      { n: "V", title: "Professionnalisme", body: "Multilingues, formés et sensibles au protocole. Les représentants d'un standard européen." },
    ],
  },
  services: {
    eyebrow: "Nos services",
    title: "UN SERVICE POUR CHAQUE OCCASION.",
    items: [
      { title: "Chauffeur privé", body: "Services de chauffeur personnel pour particuliers, familles et clients VIP." },
      { title: "Aéroports & aviation privée", body: "Transferts aéroport, terminaux VIP, FBO, aviation privée, transferts hélicoptère, meet & greet, suivi des vols et assistance bagages." },
      { title: "Mobilité executive", body: "Transport de dirigeants, réunions d'affaires, roadshows, voyages d'affaires et itinéraires multi-villes." },
      { title: "VIP & délégations", body: "Transport discret pour invités VIP, délégations diplomatiques, visites institutionnelles et déplacements sensibles au protocole." },
      { title: "Hôtels & conciergeries", body: "Transport pour hôtels de luxe, resorts, sociétés de conciergerie et leurs clients." },
      { title: "Événements & occasions", body: "Mariages, événements de mode, conférences, salons, productions cinématographiques, événements sportifs et VIP." },
      { title: "Voyages privés", body: "Services de chauffeur sur plusieurs jours, voyages européens, shopping de luxe, visites et itinéraires entièrement personnalisés." },
    ],
  },
  fleet: {
    eyebrow: "Notre flotte",
    title: "DES COLLECTIONS EXCLUSIVES.",
    onRequest: "Disponible sur demande",
    partnerNetwork: "Disponible via notre réseau de partenaires européens",
    passengers: "Passagers",
    luggage: "Bagages",
    requestVehicle: "Demander ce véhicule",
  },
  chauffeur: {
    eyebrow: "Le standard chauffeur",
    title: "NOS CHAUFFEURS FONT PLUS QUE CONDUIRE. ILS ANTICIPENT.",
    body: "Nos chauffeurs sont les représentants professionnels de l'expérience Europe Luxury Transfers.",
    standards: ["Présentation impeccable", "Tenue professionnelle", "Ponctualité", "Discrétion", "Communication multilingue", "Expertise locale", "Conduite défensive", "Maîtrise du protocole VIP", "Assistance bagages", "Service porte-à-porte", "Attention personnalisée", "Communication professionnelle"],
  },
  discretion: {
    eyebrow: "Discrétion & protocole VIP",
    title: "LA DISCRÉTION FAIT PARTIE DU SERVICE.",
    body: "Le transport VIP exige plus qu'un véhicule de luxe. Notre service est conçu autour de :",
    items: ["Confidentialité", "Vie privée", "Coordination discrète", "Déplacements sensibles au protocole", "Coordination des arrivées VIP", "Coordination multi-véhicules", "Itinéraires executive", "Coordination hôtelière", "Coordination aéroportuaire", "Gestion flexible des itinéraires"],
    statement: "CHAQUE DÉTAIL EST COORDONNÉ. CHAQUE DÉPLACEMENT EST DISCRET.",
  },
  destinations: {
    eyebrow: "France & Europe",
    title: "UN RÉSEAU. TOUTE DESTINATION.",
    body: "Europe Luxury Transfers assure un transport de luxe dans toute la France et à travers l'Europe, via un réseau européen professionnel.",
    france: "France",
    europe: "Europe",
    franceMore: "et destinations dans toute la France",
    europeMore: "et destinations dans toute l'Europe",
    statement: "FRANCE · EUROPE · TOUTE DESTINATION",
  },
  aviation: {
    eyebrow: "Aéroports & aviation privée",
    title: "DE L'AVIATION PRIVÉE À LA MOBILITÉ PRIVÉE.",
    items: ["Transferts aéroport", "Transferts terminaux VIP", "Transferts FBO", "Transport jet privé", "Transferts hélicoptère", "Meet & greet", "Suivi des vols", "Coordination vols retardés", "Assistance bagages", "Transferts hôtel–aéroport"],
  },
  corporate: {
    eyebrow: "Mobilité entreprise",
    title: "UN PARTENAIRE EUROPÉEN DE MOBILITÉ POUR LES ENTREPRISES.",
    body: "Pour les entreprises internationales, dirigeants, travel managers, hôtels de luxe, agences de voyage, conciergeries, agences événementielles et compagnies d'aviation privée.",
    items: ["Transferts executive", "Réunions d'affaires", "Roadshows", "Itinéraires multi-villes", "Chauffeurs dédiés", "Comptes entreprise", "Réservation centralisée", "Factures numériques", "Historique des réservations", "Gestion de compte dédiée"],
  },
  partners: {
    eyebrow: "Partenaires hôtels & conciergeries",
    title: "UN PARTENAIRE DE CONFIANCE POUR VOS CLIENTS.",
    body: "Nous travaillons aux côtés d'hôtels cinq étoiles, palaces, resorts de luxe, conciergeries, agences de voyage, tour-opérateurs, sociétés immobilières de luxe, wedding planners, organisateurs d'événements et compagnies d'aviation privée.",
  },
  journeys: {
    eyebrow: "Voyages européens privés",
    title: "UN VOYAGE. PLUSIEURS DESTINATIONS. UN STANDARD.",
    offers: ["Trajets simples", "Allers-retours", "Chauffeur à la journée", "Chauffeur sur plusieurs jours", "Itinéraires multi-villes", "Voyages européens sur mesure"],
  },
  pillars: {
    eyebrow: "Expérience client",
    title: "CHAQUE DÉTAIL COMPTE.",
    items: ["Ponctualité", "Discrétion", "Confort", "Professionnalisme", "Personnalisation", "Fiabilité"],
  },
  about: {
    eyebrow: "À propos d'Europe Luxury Transfers",
    title: "PLUS QU'UN TRANSFERT. UN STANDARD DE SERVICE.",
    body1: "Europe Luxury Transfers est né d'une philosophie simple : le transport de luxe doit être sans effort.",
    body2: "Les clients ne devraient pas avoir à penser au véhicule, aux horaires, à l'itinéraire, aux bagages, à la coordination ou aux petits détails. Notre équipe les anticipe.",
    body3: "Nous associons véhicules haut de gamme, chauffeurs professionnels, service multilingue et coordination opérationnelle européenne pour créer une expérience fluide du départ à l'arrivée.",
    feel: ["Attendu.", "Respecté.", "Confortable.", "Confiant.", "Maître de la situation."],
  },
  form: {
    step1: "Parlez-nous de votre trajet",
    step2: "Choisissez votre expérience",
    step3: "Personnalisez votre trajet",
    step4: "Vos coordonnées",
    pickup: "Prise en charge",
    destination: "Destination",
    date: "Date",
    time: "Heure",
    passengers: "Passagers",
    experiences: ["Executive", "Luxe", "VIP", "Van / Groupe", "Exotique", "Véhicule sur demande"],
    childSeats: "Sièges enfants",
    meetGreet: "Meet & greet",
    multipleStops: "Arrêts multiples",
    luggage: "Détails bagages",
    languagePref: "Langue préférée",
    notes: "Exigences particulières ou autres demandes",
    name: "Nom complet",
    email: "Email",
    phone: "Téléphone",
    back: "Retour",
    next: "Continuer",
    submit: "Envoyer la demande",
    successTitle: "NOTRE ÉQUIPE COORDONNERA LES DÉTAILS.",
    successBody: "Merci. Votre demande de trajet a bien été reçue. Notre équipe vous contactera rapidement avec la confirmation et les informations complètes du trajet.",
    reference: "Référence",
    company: "Société",
    contactName: "Nom du contact",
    partnerType: "Type de partenaire",
    monthlyVolume: "Trajets mensuels estimés",
    message: "Message",
    partnerTypes: ["Hôtel cinq étoiles", "Palace", "Resort de luxe", "Conciergerie", "Agence de voyage", "Tour-opérateur", "Immobilier de luxe", "Wedding planner", "Organisateur d'événements", "Aviation privée", "Autre"],
    partnerSuccess: "Merci. Notre équipe partenariats vous contactera rapidement.",
    corporateSuccess: "Merci. Notre équipe entreprises vous contactera rapidement.",
    required: "Veuillez compléter les champs requis.",
  },
  footer: {
    signature: "LA MOBILITÉ DE LUXE. SANS LIMITES.",
    tagline: "FRANCE · EUROPE · TOUTE DESTINATION",
    contact: "Contact",
    navigation: "Navigation",
    languages: "Langues",
    rights: "Tous droits réservés.",
  },
  marquee: "Discrétion · Excellence · Précision · Confort · Professionnalisme · Paris · Monaco · Genève · Milan · Londres ·",
  misc: { available247: "DISPONIBLE 24/7", availBody: "À toute heure pour les trajets planifiés et les besoins de transport, sous réserve de disponibilité des véhicules et des chauffeurs.", yourLanguage: "VOTRE LANGUE. VOTRE VOYAGE.", langBody: "Notre service international est disponible en :" },
};

const DICTS = { en, fr, de, it, es, ru, zh, ar };

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("elt-lang") || "en");
  const changeLang = useCallback((code) => {
    setLang(code);
    localStorage.setItem("elt-lang", code);
  }, []);
  const dict = DICTS[lang] || en;
  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);
  const t = useCallback((path) => {
    const get = (d) => path.split(".").reduce((o, k) => (o ? o[k] : undefined), d);
    return get(dict) ?? get(en) ?? path;
  }, [dict]);
  return <I18nContext.Provider value={{ lang, changeLang, t }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
